import {
  getNestedSubelementsByType,
  getSlsTypeByValue,
  getNestedOrFloatingElementById,
  getPersonName,
  getNextUuid,
  getOtherPersonId,
} from '@fs/zion-sls'
import { getFieldValueObject } from '../../panels/sharedHighlightPanel/utils/SharedHighlightUtils'

// ============================================================================
// Helper Functions (No Dependencies)
// ============================================================================

/**
 * Gets display label for field from slsTypes
 */
const getDisplayLabel = (field, slsTypes) => {
  const fieldType = getSlsTypeByValue(field.fieldType, 'fields', slsTypes)
  return fieldType?.displayLabel || field?.displayLabel || field?.label || field.fieldType
}

/**
 * Gets linked person IDs for a specific field
 *
 * If field is shared across multiple persons, returns array of person objects with id and partitionKey
 */
const getFieldLinkedPersons = (event, person) => {
  const personSuperElements = event?.superElements?.filter((se) => se?.id?.startsWith('1:1:')) || []
  const focusedPersonId = person ? [{ id: person.id, partitionKey: person.partitionKey }] : []

  const linkedPersonIds =
    personSuperElements.length > 0
      ? personSuperElements.map((se) => ({ id: se.id, partitionKey: se.partitionKey }))
      : focusedPersonId

  // Event's superElements contain person IDs and partitionKeys
  return linkedPersonIds || []
}

/**
 * Creates empty date field for new events
 */
const createEmptyDateField = (slsTypes) => {
  const dateFieldType = getSlsTypeByValue('DATE', 'fields', slsTypes)
  return {
    id: getNextUuid(),
    type: dateFieldType?.fieldType || 'DATE',
    displayLabel: dateFieldType?.displayLabel || 'Date',
    elementId: null, // Will be created during save
    value: {
      originalText: null,
      standard: null,
    },
    linkedPersonIds: [],
    added: true, // New field
    removed: false,
    editable: true,
    dispatches: [],
  }
}

/**
 * Creates empty place field for new events
 */
const createEmptyPlaceField = (slsTypes) => {
  const placeFieldType = getSlsTypeByValue('PLACE', 'fields', slsTypes)
  return {
    id: getNextUuid(),
    type: placeFieldType?.fieldType || 'PLACE',
    displayLabel: placeFieldType?.displayLabel || 'Place',
    elementId: null, // Will be created during save
    value: {
      originalText: null,
      standard: null,
    },
    linkedPersonIds: [],
    added: true,
    removed: false,
    editable: true,
    dispatches: [],
  }
}

/**
 * Creates completely empty event (for validation)
 *
 * If emptyFields is true, creates event with empty dates and places arrays
 */
const createEmptyEvent = (isPrimary, slsTypes, person, emptyFields = false) => {
  return {
    id: getNextUuid(),
    partitionKey: person?.partitionKey,
    existingEventId: null,
    existingEventTypeFieldId: null,
    eventType: null,
    primary: isPrimary,
    linkedPersonIds: getFieldLinkedPersons(null, person),
    confirmedUnlinks: [],
    confirmedAddLinks: [],
    relationship: null,
    shared: false,
    dates: emptyFields ? [] : [createEmptyDateField(slsTypes)],
    places: emptyFields ? [] : [createEmptyPlaceField(slsTypes)],
    other: [],
    added: true, // New event
  }
}

/**
 * Checks if the given eventSlsType contains the 'RELATIONSHIP' element type.
 *
 * @param {Object} eventSlsType - The event type object to check.
 * @param {string[]} [eventSlsType.elementTypes] - Array of element type strings.
 * @returns {boolean} True if 'RELATIONSHIP' is included in elementTypes, otherwise false.
 */
export const containsRelationshipType = (eventSlsType) => {
  return eventSlsType?.elementTypes?.includes('RELATIONSHIP')
}

// ============================================================================
// Field Extraction Functions (Depend on helpers above)
// ============================================================================

/**
 * Extracts date fields from event, flattening from nested structure
 */
const extractDateFields = (event, slsTypes, person) => {
  const dateFieldsAndElements = getNestedSubelementsByType(event, 'DATE')
  const dateElements = dateFieldsAndElements.filter((e) => e.elementType === 'DATE')

  const fields = []

  dateElements?.forEach((dateElement) => {
    const dateFields = dateElement?.subElements?.filter((sub) => sub.elementType === 'FIELD') || []

    dateFields.forEach((field) => {
      fields.push({
        id: field.id,
        type: field?.fieldType || 'DATE',
        displayLabel: getDisplayLabel(field, slsTypes),
        elementId: dateElement?.id, // Parent DATE intermediary ID
        value: getFieldValueObject(field),
        linkedPersonIds: getFieldLinkedPersons(event, person),
        added: false,
        removed: false,
        editable: field?.typeData?.editable ?? false,
        dispatches: [],
      })
    })
  })

  return fields
}

/**
 * Extracts place fields (same pattern as dates)
 */
const extractPlaceFields = (event, slsTypes, person) => {
  const placeFieldsAndElements = getNestedSubelementsByType(event, 'PLACE')
  const placeElements = placeFieldsAndElements.filter((e) => e.elementType === 'PLACE')
  const fields = []

  placeElements?.forEach((placeElement) => {
    const placeFields = placeElement?.subElements?.filter((sub) => sub.elementType === 'FIELD') || []

    placeFields.forEach((field) => {
      fields.push({
        id: field.id,
        type: field?.fieldType || 'PLACE',
        displayLabel: getDisplayLabel(field, slsTypes),
        elementId: placeElement?.id, // Parent PLACE intermediary ID
        value: getFieldValueObject(field),
        linkedPersonIds: getFieldLinkedPersons(event, person),
        added: false,
        removed: false,
        editable: field?.typeData?.editable ?? false,
        dispatches: [],
      })
    })
  })

  return fields
}

/**
 * Extracts other fields (direct FIELD elements, no intermediary)
 */
const extractOtherFields = (event, slsTypes, person) => {
  const allEventFields = event?.subElements?.filter((sub) => sub.elementType === 'FIELD') || []
  const otherFields = allEventFields.filter((field) => !['DATE', 'PLACE', 'EVENT_TYPE'].includes(field?.fieldType)) // Exclude DATE, PLACE, and EVENT_TYPE fields

  return otherFields.map((field) => ({
    id: field.id,
    type: field?.fieldType,
    displayLabel: getDisplayLabel(field, slsTypes),
    elementId: event?.id, // Directly under event, no intermediary
    value: getFieldValueObject(field),
    linkedPersonIds: getFieldLinkedPersons(event, person),
    added: false,
    removed: false,
    editable: field?.typeData?.editable ?? false,
    dispatches: [],
  }))
}

/**
 * Extracts event type information
 */
const parseEventType = (event, slsTypes) => {
  const { eventType } = event
  const slsEventType = getSlsTypeByValue(eventType, 'events', slsTypes)

  const isRelationshipEvent = slsEventType?.elementTypes?.includes('RELATIONSHIP') || false

  return {
    key: eventType,
    primaryText: slsEventType?.displayLabel || eventType,
    isRelationshipEvent,
    relType: isRelationshipEvent ? slsEventType?.relType : null,
  }
}

/**
 * Retrieves related person data based on relationship information.
 *
 * @param {Object} relationshipData - The relationship data object.
 * @param {Object} person - The current person object.
 * @param {Array} images - Array of images from SLS state context.
 * @returns {Object} An object with the related person's ID as `key` and their name as `primaryText`.
 */
const getRelatedPersonData = (relationshipData, person, images) => {
  const otherPersonId = getOtherPersonId(relationshipData, person)
  const otherPerson = getNestedOrFloatingElementById(images, otherPersonId)

  return {
    key: otherPersonId,
    primaryText: getPersonName({ person: otherPerson, justOneName: true }) || 'unknown',
    recordId: otherPerson?.recordId || null, // For quick checking of cross-record relationships
    partitionKey: otherPerson?.partitionKey || null,
  }
}

/**
 * Parses relationship data for relationship events
 */
const parseRelationshipData = (event, eventType, person, images, slsTypes) => {
  const { relationshipData } = event
  const slsEventType = getSlsTypeByValue(eventType?.key, 'events', slsTypes)
  const isRelationshipEvent = containsRelationshipType(slsEventType) || false

  if (!isRelationshipEvent || !relationshipData) {
    return null
  }

  // If this is a relationship event, get the related person data
  const otherPerson = getRelatedPersonData(relationshipData, person, images)

  // Find other person in relationship
  const otherPersonId = relationshipData.superElements?.find((se) => se.id !== person.id)?.id

  return {
    id: relationshipData.id,
    data: relationshipData,
    otherPerson: otherPersonId ? otherPerson : null,
  }
}

// ============================================================================
// Event Transformation Functions (Depend on field extraction above)
// ============================================================================

/**
 * Ensures that a field array contains at least one field with the specified fieldType
 * If not found, adds an empty field of that type
 *
 * @param {Array} fields - Array of field objects
 * @param {string} fieldType - The field type to ensure exists ('DATE' or 'PLACE')
 * @param {Object} slsTypes - SLS types object for getting displayLabels
 * @returns {Array} Field array with guaranteed standard field
 */
const ensureStandardFieldExists = (fields, fieldType, slsTypes) => {
  // Check if a field with the specified fieldType already exists
  const hasStandardField = fields.some((field) => field.type === fieldType)

  if (hasStandardField) {
    return fields
  }

  // Add the missing standard field
  const createEmptyField = fieldType === 'DATE' ? createEmptyDateField : createEmptyPlaceField
  return [...fields, createEmptyField(slsTypes)]
}

/**
 * Converts SLS event to simplified form structure
 *
 * Output matches structure from simplified-events-structure.md
 */
export const parseEventToSimplifiedForm = (event, slsTypes, person, images) => {
  // Get event type info
  const eventType = parseEventType(event, slsTypes)

  // Extract and flatten field arrays and data
  const dates = extractDateFields(event, slsTypes, person)
  const places = extractPlaceFields(event, slsTypes, person)
  const other = extractOtherFields(event, slsTypes, person)
  const linkedPersonIds = getFieldLinkedPersons(event, person)
  const existingEventTypeFieldId = getNestedSubelementsByType(event, 'EVENT_TYPE')[0]?.id || null

  // Handle relationship events
  const relationship = parseRelationshipData(event, eventType, person, images, slsTypes)

  // Ensure at least one date/place field exists, and that a standard DATE/PLACE field exists
  const ensuredDates =
    dates.length > 0 ? ensureStandardFieldExists(dates, 'DATE', slsTypes) : [createEmptyDateField(slsTypes)]
  const ensuredPlaces =
    places.length > 0 ? ensureStandardFieldExists(places, 'PLACE', slsTypes) : [createEmptyPlaceField(slsTypes)]

  return {
    id: event.id,
    partitionKey: event?.partitionKey,
    existingEventId: event.id, // Same as id for existing events
    existingEventTypeFieldId,
    eventType,
    primary: event.primary || false,
    linkedPersonIds,
    confirmedUnlinks: [], // Initialize empty, populated by form interactions
    confirmedAddLinks: [], // Initialize empty, populated by form interactions
    relationship, // null if not a relationship event
    shared: linkedPersonIds.length > 1,
    dates: ensuredDates,
    places: ensuredPlaces,
    other,
    added: !event?.id, // If no ID, this is a new event
  }
}

/**
 * Ensures at least one primary event exists
 *
 * Same logic as original but uses simplified structure
 */
const ensurePrimaryEvent = (person, images, slsTypes, primaryEvents) => {
  if (primaryEvents.length > 0) {
    return primaryEvents
  }

  // Try to get record primary event
  const record = getNestedOrFloatingElementById(images, person.recordId)
  const recordPrimaryEvent = getNestedSubelementsByType(record, 'EVENT')?.find((e) => e.primary === true)

  if (recordPrimaryEvent) {
    return [parseEventToSimplifiedForm(recordPrimaryEvent, slsTypes, person, images)]
  }

  // Create empty primary event for validation
  return [createEmptyEvent(true, slsTypes, person)]
}

/**
 * Separates events into primary and additional arrays
 */
const separateEventsByPrimaryFlag = (events, slsTypes, person, images) => {
  const primary = []
  const additional = []

  events?.forEach((event) => {
    const parsedEvent = parseEventToSimplifiedForm(event, slsTypes, person, images)
    if (parsedEvent?.primary) {
      primary.push(parsedEvent)
    } else {
      additional.push(parsedEvent)
    }
  })

  return { primary, additional }
}

// ============================================================================
// Main Export Function
// ============================================================================

/**
 * Parses SLS events into simplified form structure.
 *
 * @param {Array} events - Array of SLS event elements from person
 * @param {Object} slsTypes - SLS type definitions for events, fields, etc.
 * @param {Object} person - The person element these events belong to
 * @param {Object} images - Full SLS images data for lookups
 * @returns {Object} Simplified form structure with events, primaryEventId
 */
export function parseEventsForForm(events, slsTypes, person, images) {
  // Step 1: Separate into primary and additional
  const { primary: primaryEvents, additional: additionalEvents } = separateEventsByPrimaryFlag(
    events,
    slsTypes,
    person,
    images
  )

  // Step 2: Ensure at least one primary event exists
  const finalPrimaryEvents = ensurePrimaryEvent(person, images, slsTypes, primaryEvents)

  return {
    events: {
      primary: finalPrimaryEvents,
      additional: additionalEvents,
    },
    primaryEventId: finalPrimaryEvents[0]?.id || null,
  }
}

/**
 * Creates an empty event with a pre-filled event type.
 * Used by suggestion buttons to create events with specific types.
 *
 * @param {string} eventType - The event type value (e.g., 'BIRTH', 'MARRIAGE')
 * @param {Object} slsTypes - SLS types object for type lookup
 * @param {Object} person - The person element these events belong to
 * @param {boolean} isPrimary - Whether the event should be marked as primary
 * @returns {Object} Event object with pre-filled eventType
 */
export function createEmptyEventWithType(eventType, slsTypes, person, isPrimary = false) {
  const emptyEvent = createEmptyEvent(isPrimary, slsTypes, person) // Create empty event with empty fields

  if (!eventType) return emptyEvent

  const slsEventType = getSlsTypeByValue(eventType, 'events', slsTypes)
  const isRelationshipEvent = containsRelationshipType(slsEventType) || false

  return {
    ...emptyEvent,
    eventType: {
      key: eventType,
      primaryText: slsEventType?.displayLabel || eventType,
      isRelationshipEvent,
      relType: slsEventType?.relType || null,
    },
  }
}
